import * as React from "react";
import { observer } from "mobx-react-lite";
import {
    Button,
    Table,
    Switch,
    Popconfirm,
    Layout,
    Tooltip,
    Select,
    Checkbox,
    Input,
    Tag,
} from "antd";
import { ColumnType } from "antd/lib/table";
import { TaskType, TaskTypeDueDate } from "../types";
import TaskTypesVisualStore from "../stores/TaskTypesVisualStore";
import TaskTypeCreateDialog from "./TaskTypeCreateDialog";
import TaskTypeEditDialog from "./TaskTypeEditDialog";
import { ProjectsStore } from "../../common/stores";
import { safeStringLocaleCompare, Utils } from "../../common/misc/Utils";

const { Content } = Layout;
type Props = {
    store: TaskTypesVisualStore;
    projectsStore: ProjectsStore;
};

const TaskTypesList: React.FC<Props> = ({ store, projectsStore }) => {
    React.useEffect(() => {
        return () => {
            store.clearSelectedProject();
        };
    }, []);

    const visibleTaskTypes = store.filteredTaskTypes;
    const hasSelectedProject = Boolean(store.selectedProjectId);
    const hasCustomTaskTypes = store.taskTypes.some(
        (taskType) => !taskType.isSystemDefault,
    );
    const shouldShowEmptyState =
        hasSelectedProject && !store.taskTypesLoading && !hasCustomTaskTypes;

    const getUserInacitivityLabel = (userId?: string | null) => {
        const isDeleted = store.usersForProject.some(
            (u) => u.userId === userId && u.isDeleted,
        );
        return (
            <span data-id-cells="is-active" style={{ color: "red" }}>
                {isDeleted ? " (Inactive)" : ""}
            </span>
        );
    };

    const periodResolver = (dueDate: TaskTypeDueDate) => {
        const isSingular = dueDate.count === 1;

        switch (dueDate.period) {
            case "D":
                return isSingular ? "day" : "days";
            case "W":
                return isSingular ? "week" : "weeks";
            case "M":
                return isSingular ? "month" : "months";
            case "Y":
                return isSingular ? "year" : "years";
            default:
                return "";
        }
    };

    const columns: ColumnType<TaskType | null>[] = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (_, record) => (
                <span data-id-cells="name">
                    {record?.name}
                    {record?.isSystemDefault ? (
                        <Tag
                            className="alpha-info-tag"
                            style={{ marginLeft: 8 }}
                        >
                            System managed
                        </Tag>
                    ) : null}
                </span>
            ),
            sorter: (a, b) =>
                safeStringLocaleCompare(b?.name || "", a?.name || ""),
        },
        {
            title: "Assignee",
            dataIndex: "assignee",
            key: "assignee",
            render: (_, record) => (
                <>
                    <span data-id-cells="assignee">
                        {store.getUserNameById(record?.assignee)}
                    </span>
                    {getUserInacitivityLabel(record?.assignee)}
                </>
            ),
            sorter: (a, b) => {
                store.getUserNameById(a?.assignee);
                return safeStringLocaleCompare(
                    store.getUserNameById(b?.assignee),
                    store.getUserNameById(a?.assignee),
                );
            },
        },
        {
            title: "Code",
            dataIndex: "code",
            key: "code",
            render: (_, record) => (
                <span data-id-cells="code">{record?.code}</span>
            ),
            sorter: (a, b) =>
                safeStringLocaleCompare(b?.code || "", a?.code || ""),
        },
        {
            title: "Due period",
            dataIndex: "dueDate",
            key: "duePeriod",
            render: (_, record) => (
                <span data-id-cells="code">
                    {record?.dueDate?.count
                        ? `${record.dueDate.count} ${periodResolver(record.dueDate)}`
                        : ""}
                </span>
            ),
        },
        {
            title: "Reminder days",
            dataIndex: "reminderDays",
            key: "reminderDays",
            render: (_, record) => (
                <span data-id-cells="reminder-days">
                    {record?.reminderDays ?? ""}
                </span>
            ),
        },
        {
            title: "Manual status change",
            dataIndex: "canChangeStatusManually",
            key: "canChangeStatusManually",
            render: (_, record) => (
                <Checkbox disabled checked={record?.canChangeStatusManually} />
            ),
            sorter: (a, b) => {
                if (a?.canChangeStatusManually === b?.canChangeStatusManually)
                    return 0;
                return a?.canChangeStatusManually ? -1 : 1;
            },
        },
        {
            title: "Actions",
            width: 200,
            render: (_, record) => (
                <div className="row-actions-wrapper">
                    <Button
                        data-id-cells="Edit type"
                        title="Rename"
                        size="small"
                        type="link"
                        disabled={record?.isSystemDefault}
                        onClick={() => {
                            if (record) {
                                store.selectType(record);
                            }
                        }}
                    >
                        <Tooltip
                            title={
                                record?.isSystemDefault
                                    ? "System default types are read-only"
                                    : "Edit type"
                            }
                            placement="bottom"
                        >
                            <i className="alpha-icon md table-action-edit" />
                        </Tooltip>
                    </Button>
                    <Popconfirm
                        id="data-id-popconfirm-box"
                        title="Are you sure you want to delete this type?"
                        onConfirm={() => {
                            if (record) {
                                store.deleteTaskType(record.id);
                            }
                        }}
                        placement="topRight"
                        disabled={record?.isSystemDefault}
                    >
                        <Button
                            data-id-cells="Delete type"
                            title="Delete"
                            size="small"
                            type="link"
                            disabled={record?.isSystemDefault}
                        >
                            <Tooltip
                                title={
                                    record?.isSystemDefault
                                        ? "System default types are read-only"
                                        : "Delete type"
                                }
                                placement="bottom"
                            >
                                <i className="alpha-icon md table-action-delete" />
                            </Tooltip>
                        </Button>
                    </Popconfirm>
                    <Switch
                        data-id-cells="is-active"
                        data-id-value={record?.isEnabled}
                        style={{
                            visibility: "visible",
                        }}
                        checked={record?.isEnabled}
                        disabled={record?.isSystemDefault}
                        onChange={() => {
                            if (record) {
                                store.toggleTaskType(record.id);
                            }
                        }}
                    />
                </div>
            ),
        },
    ];

    const handleEnterPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        store.setSearchString(event.currentTarget.value);
    };

    return (
        <Layout className="screen-size tasks-types-layout layout-with-table">
            <div className="header-wrapper">
                <div className="title-wrapper">
                    <div className="header-title">Task types</div>
                </div>
                <Button
                    key={1}
                    disabled={!store.selectedProjectId}
                    data-id="button-add-type"
                    type="primary"
                    size="large"
                    onClick={() => {
                        store.setNewTypeDialogVisible(true);
                    }}
                >
                    Add type
                </Button>
            </div>
            <TaskTypeCreateDialog store={store} />
            {store.typeEditDialogVisible ? (
                <TaskTypeEditDialog store={store} />
            ) : null}
            <Layout>
                <Content>
                    <div className="projects-list-container">
                        <Select
                            style={{ width: 220 }}
                            value={store.selectedProjectId || undefined}
                            options={
                                projectsStore.administrableProjects?.map(
                                    (p) => ({ label: p.name, value: p.id }),
                                ) || []
                            }
                            placeholder="Select project"
                            onChange={store.setSelectedProjectId}
                            showSearch
                            filterOption={Utils.filterOption}
                        />
                        <Input
                            data-id="search area"
                            placeholder="Search..."
                            onPressEnter={handleEnterPress}
                            onClear={() => store.setSearchString("")}
                            maxLength={150}
                            style={{ width: 200, marginLeft: "auto" }}
                            prefix={
                                <i
                                    className="alpha-icon xs table-search"
                                    style={{ marginRight: 10 }}
                                />
                            }
                            allowClear
                        />
                    </div>
                    <Table
                        data-id="table-tasks-types-list"
                        className="alpha-table task-types"
                        loading={store.taskTypesLoading}
                        columns={columns}
                        dataSource={hasSelectedProject ? visibleTaskTypes : []}
                        rowKey={(r) => r!.id}
                        pagination={false}
                        scroll={{ y: "calc(100vh - 222px)" }}
                    />
                    {shouldShowEmptyState ? (
                        <div className="task-types-empty-state">
                            <div className="task-types-empty-state-title">
                                No custom task types
                            </div>
                            <div className="task-types-empty-state-description">
                                Standard tasks do not need task types. Create a
                                custom task type only if this project needs
                                different defaults or workflows.
                            </div>
                            <Button
                                data-id="button-create-custom-task-type"
                                type="primary"
                                size="large"
                                onClick={() => {
                                    store.setNewTypeDialogVisible(true);
                                }}
                            >
                                Create custom task type
                            </Button>
                        </div>
                    ) : null}
                </Content>
            </Layout>
        </Layout>
    );
};

export default observer(TaskTypesList);
